package com.example.positionahead.model

import kotlinx.serialization.Serializable

enum class AssetClass {
    STOCK,
    ETF,
    BOND,
    REIT,
    CASH,
}

@Serializable
data class PositionLot(
    val id: String,
    val shares: Double,
    val costBasisPerShare: Double,
    val purchaseDate: String? = null,
    val note: String? = null,
)

@Serializable
data class Position(
    val id: String,
    val symbol: String,
    val assetClass: AssetClass = AssetClass.STOCK,
    val lots: List<PositionLot>,
)

fun Position.totalShares(): Double = lots.sumOf { it.shares }

fun Position.totalCostBasis(): Double = lots.sumOf { it.shares * it.costBasisPerShare }

fun Position.averageCostBasisPerShare(): Double {
    val shares = totalShares()
    return if (shares > 0) totalCostBasis() / shares else 0.0
}

fun Position.withAddedLot(lot: PositionLot): Position = copy(lots = lots + lot)

fun Position.withUpdatedLot(
    lotId: String,
    shares: Double,
    costBasisPerShare: Double,
    purchaseDate: String?,
    note: String?,
): Position = copy(
    lots = lots.map { lot ->
        if (lot.id == lotId) {
            lot.copy(
                shares = shares,
                costBasisPerShare = costBasisPerShare,
                purchaseDate = purchaseDate,
                note = note,
            )
        } else lot
    },
)

fun Position.withoutLot(lotId: String): Position = copy(lots = lots.filterNot { it.id == lotId })

fun Position.withSoldLot(lotId: String, sharesToSell: Double): Position {
    val lot = lots.firstOrNull { it.id == lotId } ?: return this
    return if (sharesToSell >= lot.shares) {
        withoutLot(lotId)
    } else {
        copy(
            lots = lots.map {
                if (it.id == lotId) it.copy(shares = it.shares - sharesToSell) else it
            },
        )
    }
}

fun Position.earliestPurchaseDate(): String? =
    lots.mapNotNull { it.purchaseDate }.minOrNull()

fun Position.latestPurchaseDate(): String? =
    lots.mapNotNull { it.purchaseDate }.maxOrNull()

fun Position.purchaseDateLabel(): String {
    val earliest = earliestPurchaseDate()
    val latest = latestPurchaseDate()
    return when {
        earliest == null -> "No purchase date"
        latest == null || earliest == latest -> "Purchased $earliest"
        else -> "Purchased $earliest – $latest"
    }
}

fun Position.deepCopy(newId: String = id): Position = copy(
    id = newId,
    lots = lots.map { it.copy(id = "${newId}-${it.id}") },
)

fun positionWithSingleLot(
    id: String,
    symbol: String,
    shares: Double,
    costBasisPerShare: Double,
    assetClass: AssetClass = AssetClass.STOCK,
    purchaseDate: String? = null,
    note: String? = null,
): Position = Position(
    id = id,
    symbol = symbol.uppercase(),
    assetClass = assetClass,
    lots = listOf(
        PositionLot(
            id = "$id-lot-0",
            shares = shares,
            costBasisPerShare = costBasisPerShare,
            purchaseDate = purchaseDate,
            note = note,
        ),
    ),
)
